/**
 * /contacts — global contacts browser.
 *
 * Contacts in ledger-core are scoped to a Party (vendor / customer /
 * subcontractor).  There is no GET /api/contacts list endpoint — you
 * must know the party_id to fetch contacts.  This page therefore
 * provides:
 *
 *   1. A search bar that filters the full parties list in real time.
 *   2. When a party is selected, its contacts are fetched and shown in
 *      a table with inline Add / Edit / Delete.
 *   3. An "Add Contact" dialog that creates via POST /api/contacts with
 *      the exact ContactCreate schema fields.
 *
 * Navigation hint: contacts can also be accessed from the vendor or
 * customer detail pages, which use the same contactsAtPartyQueryOptions.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { api } from "@/lib/api";
import {
  contactKeys,
  contactsAtPartyQueryOptions,
  partiesListQueryOptions,
} from "@/lib/queries";
import { useToast } from "@/components/Toaster";
import type { Contact, Party } from "@/lib/types";
import { codeFor } from "../lib/displayCode";

export const Route = createFileRoute("/_auth/contacts")({
  component: ContactsPage,
});

// ──────────────────────────────────────────────────────────────
// Page shell
// ──────────────────────────────────────────────────────────────
function ContactsPage() {
  const [search,          setSearch]          = useState("");
  const [selectedParty,   setSelectedParty]   = useState<Party | null>(null);
  const [addOpen,         setAddOpen]         = useState(false);

  // Fetch all parties (we filter client-side — the parties list is
  // typically small enough that a full load is fine).
  const partiesQuery = useQuery(partiesListQueryOptions());
  const parties      = (partiesQuery.data ?? []).filter((p) =>
    search.trim() === ""
      ? true
      : p.name.toLowerCase().includes(search.toLowerCase()) ||
        p.code.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Contacts</h1>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            people at a vendor / customer / sub
          </span>
          <div className="flex-1" />
          {selectedParty && (
            <button
              onClick={() => setAddOpen(true)}
              className="text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 font-medium"
            >
              + Add contact
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Left panel — party search */}
        <div className="w-72 flex-shrink-0 border-r border-slate-200 dark:border-gray-800 flex flex-col bg-slate-50 dark:bg-gray-950">
          <div className="p-3 border-b border-slate-200 dark:border-gray-800">
            <TextField
              size="small"
              placeholder="Search parties…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              inputProps={{ "aria-label": "search parties" }}
            />
          </div>
          <div className="flex-1 overflow-y-auto">
            {partiesQuery.isLoading && (
              <div className="text-xs text-slate-400 dark:text-slate-500 p-3">Loading…</div>
            )}
            {!partiesQuery.isLoading && parties.length === 0 && (
              <div className="text-xs text-slate-400 dark:text-slate-500 p-3">
                {search ? "No parties match." : "No parties yet."}
              </div>
            )}
            {parties.map((p) => (
              <button
                key={p.id}
                onClick={() => setSelectedParty(p)}
                className={[
                  "w-full text-left px-3 py-2.5 border-b border-slate-100 dark:border-gray-800",
                  "hover:bg-white dark:hover:bg-gray-900 transition-colors",
                  selectedParty?.id === p.id
                    ? "bg-blue-50 dark:bg-blue-950 border-l-2 border-l-blue-500"
                    : "",
                ].join(" ")}
              >
                <div className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">
                  {p.name}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  {codeFor(p) && <>{codeFor(p)}{" · "}</>}
                  <span className="capitalize">{p.party_type}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Right panel — contacts for selected party */}
        <div className="flex-1 overflow-auto p-6">
          {!selectedParty ? (
            <NoPartySelected />
          ) : (
            <PartyContactsPanel party={selectedParty} />
          )}
        </div>
      </div>

      {/* Add contact dialog */}
      {selectedParty && addOpen && (
        <AddContactDialog
          open={addOpen}
          partyId={selectedParty.id}
          partyName={selectedParty.name}
          onClose={() => setAddOpen(false)}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Empty state
// ──────────────────────────────────────────────────────────────
function NoPartySelected() {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center gap-3 py-16">
      <div className="text-4xl select-none">👤</div>
      <Typography variant="h6" sx={{ fontWeight: 500, fontSize: "1rem" }}>
        Select a party to view contacts
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360 }}>
        Contacts are scoped to a party (vendor, customer, or subcontractor).
        Search or scroll the list on the left to pick one, then add or manage
        its contacts here.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360, mt: 1 }}>
        Contacts can also be accessed from{" "}
        <Link to="/vendors" className="underline text-blue-600 hover:text-blue-700">
          Vendors
        </Link>{" "}
        and{" "}
        <Link to="/customers" className="underline text-blue-600 hover:text-blue-700">
          Customers
        </Link>{" "}
        detail pages.
      </Typography>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Party contacts panel
// ──────────────────────────────────────────────────────────────
function PartyContactsPanel({ party }: { party: Party }) {
  const qc    = useQueryClient();
  const toast = useToast();
  const [editContact, setEditContact] = useState<Contact | null>(null);

  const contactsQuery = useQuery(contactsAtPartyQueryOptions(party.id));
  const contacts      = contactsQuery.data ?? [];

  const deleteContact = useMutation({
    mutationFn: (id: string) =>
      api(`/api/contacts/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contactKeys.forParty(party.id) });
      toast.success("Contact deleted");
    },
    onError: (err: Error) => toast.error(`Delete failed: ${err.message}`),
  });

  return (
    <div className="space-y-4">
      {/* Party heading */}
      <div className="flex items-center gap-2">
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1rem" }}>
          {party.name}
        </Typography>
        <Chip
          label={party.party_type}
          size="small"
          sx={{ fontSize: "0.7rem", textTransform: "capitalize" }}
        />
        <Typography variant="body2" color="text.secondary">
          {contacts.length} contact{contacts.length !== 1 ? "s" : ""}
        </Typography>
      </div>

      {contactsQuery.isLoading && (
        <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>
      )}

      {!contactsQuery.isLoading && contacts.length === 0 && (
        <div className="text-sm text-slate-500 dark:text-slate-400 py-8 text-center">
          No contacts yet. Use <span className="font-medium">+ Add contact</span> above.
        </div>
      )}

      {contacts.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md dark:bg-gray-900 dark:border-gray-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600 dark:bg-gray-950 dark:text-slate-300">
              <tr>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">Title</th>
                <th className="text-left px-4 py-2">Email</th>
                <th className="text-left px-4 py-2">Phone</th>
                <th className="text-center px-4 py-2">Primary</th>
                <th className="text-left px-4 py-2">Notes</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {contacts.map((c) => (
                <tr
                  key={c.id}
                  className="border-t border-slate-100 dark:border-gray-800 hover:bg-slate-50 dark:hover:bg-gray-800"
                >
                  <td className="px-4 py-2 font-medium">
                    {c.name}
                    {c.is_primary && (
                      <Chip
                        label="Primary"
                        size="small"
                        color="primary"
                        sx={{ ml: 1, fontSize: "0.65rem", height: 18 }}
                      />
                    )}
                  </td>
                  <td className="px-4 py-2 text-slate-600 dark:text-slate-300">
                    {c.title ?? <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-2">
                    {c.email ? (
                      <a href={`mailto:${c.email}`} className="text-blue-600 hover:underline dark:text-blue-400">
                        {c.email}
                      </a>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-slate-600 dark:text-slate-300">
                    {c.phone ?? <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-2 text-center">
                    {c.is_primary ? "✓" : ""}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500 dark:text-slate-400 max-w-[200px] truncate">
                    {c.notes ?? ""}
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => setEditContact(c)}
                        sx={{ mr: 0.5 }}
                      >
                        <PencilIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          if (confirm(`Delete contact "${c.name}"?`)) {
                            deleteContact.mutate(c.id);
                          }
                        }}
                      >
                        <TrashIcon />
                      </IconButton>
                    </Tooltip>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editContact && (
        <EditContactDialog
          open
          contact={editContact}
          partyId={party.id}
          onClose={() => setEditContact(null)}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Add contact dialog
// ContactCreate schema: name, title?, email?, phone?,
//   is_primary (bool, def false), notes?, party_id
// ──────────────────────────────────────────────────────────────
function AddContactDialog({
  open,
  partyId,
  partyName,
  onClose,
}: {
  open: boolean;
  partyId: string;
  partyName: string;
  onClose: () => void;
}) {
  const qc    = useQueryClient();
  const toast = useToast();

  const [name,      setName]      = useState("");
  const [title,     setTitle]     = useState("");
  const [email,     setEmail]     = useState("");
  const [phone,     setPhone]     = useState("");
  const [isPrimary, setIsPrimary] = useState(false);
  const [notes,     setNotes]     = useState("");

  const create = useMutation({
    mutationFn: () =>
      api<Contact>("/api/contacts", {
        method: "POST",
        body: {
          party_id:   partyId,
          name,
          title:      title  || null,
          email:      email  || null,
          phone:      phone  || null,
          is_primary: isPrimary,
          notes:      notes  || null,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contactKeys.forParty(partyId) });
      toast.success("Contact added");
      onClose();
    },
    onError: (err: Error) => toast.error(`Failed: ${err.message}`),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add contact — {partyName}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField size="small" label="Name *" value={name}
                     onChange={(e) => setName(e.target.value)} fullWidth />
          <TextField size="small" label="Title" value={title}
                     onChange={(e) => setTitle(e.target.value)} fullWidth />
          <TextField size="small" label="Email" type="email" value={email}
                     onChange={(e) => setEmail(e.target.value)} fullWidth />
          <TextField size="small" label="Phone" value={phone}
                     onChange={(e) => setPhone(e.target.value)} fullWidth />
          <TextField size="small" label="Notes" value={notes}
                     onChange={(e) => setNotes(e.target.value)}
                     multiline minRows={2} fullWidth />
          <FormControlLabel
            control={
              <Switch
                checked={isPrimary}
                onChange={(e) => setIsPrimary(e.target.checked)}
                size="small"
              />
            }
            label={
              <span className="text-sm">
                Primary contact
                <span className="text-xs text-slate-500 ml-1">
                  (shown first; only one per party should be primary)
                </span>
              </span>
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!name.trim() || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? "Saving…" : "Add contact"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ──────────────────────────────────────────────────────────────
// Edit contact dialog
// ContactUpdate schema: all fields optional
// ──────────────────────────────────────────────────────────────
function EditContactDialog({
  open,
  contact,
  partyId,
  onClose,
}: {
  open: boolean;
  contact: Contact;
  partyId: string;
  onClose: () => void;
}) {
  const qc    = useQueryClient();
  const toast = useToast();

  const [name,      setName]      = useState(contact.name);
  const [title,     setTitle]     = useState(contact.title  ?? "");
  const [email,     setEmail]     = useState(contact.email  ?? "");
  const [phone,     setPhone]     = useState(contact.phone  ?? "");
  const [isPrimary, setIsPrimary] = useState(contact.is_primary);
  const [notes,     setNotes]     = useState(contact.notes  ?? "");

  const update = useMutation({
    mutationFn: () =>
      api<Contact>(`/api/contacts/${contact.id}`, {
        method: "PATCH",
        body: {
          name,
          title:      title || null,
          email:      email || null,
          phone:      phone || null,
          is_primary: isPrimary,
          notes:      notes || null,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contactKeys.forParty(partyId) });
      toast.success("Contact updated");
      onClose();
    },
    onError: (err: Error) => toast.error(`Failed: ${err.message}`),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit contact</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField size="small" label="Name *" value={name}
                     onChange={(e) => setName(e.target.value)} fullWidth />
          <TextField size="small" label="Title" value={title}
                     onChange={(e) => setTitle(e.target.value)} fullWidth />
          <TextField size="small" label="Email" type="email" value={email}
                     onChange={(e) => setEmail(e.target.value)} fullWidth />
          <TextField size="small" label="Phone" value={phone}
                     onChange={(e) => setPhone(e.target.value)} fullWidth />
          <TextField size="small" label="Notes" value={notes}
                     onChange={(e) => setNotes(e.target.value)}
                     multiline minRows={2} fullWidth />
          <FormControlLabel
            control={
              <Switch
                checked={isPrimary}
                onChange={(e) => setIsPrimary(e.target.checked)}
                size="small"
              />
            }
            label={<span className="text-sm">Primary contact</span>}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!name.trim() || update.isPending}
          onClick={() => update.mutate()}
        >
          {update.isPending ? "Saving…" : "Save changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ──────────────────────────────────────────────────────────────
// Micro icon components (avoids adding an icon library dep)
// ──────────────────────────────────────────────────────────────
function PencilIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}
